import React, { useState } from 'react';
import { Box, TextField, InputAdornment, Button } from '@mui/material';
import { useVehicleReservation } from '../../common/Providers/VehicleReservationProvider/UseVehicleReservation';
import { useStoreDetails } from '../../common/Providers/StoreDetailsProvider/UseStoreDetails';

interface Props {
  isDisableDiscount?: boolean;
}

const DiscountCodes: React.FC<Props> = ({ isDisableDiscount }) => {
  const { VehicleReservationMain, setVehicleReservationValue } = useVehicleReservation();
  const { discounts } = useStoreDetails();

  const [inputCode, setInputCode] = useState<string>(VehicleReservationMain.discount_code || '');
  const [validation, setValidation] = useState<boolean | null>(null);

  const applyDiscount = () => {
    if (inputCode.trim() === '') {
      setVehicleReservationValue('promo_code', null);
      setVehicleReservationValue('discount_rate', null);
      setVehicleReservationValue('discountApplied', false);
      setValidation(null);
      return;
    }

    const selectedDiscount: any = discounts.find((item: any) => {
      if (typeof item.code === 'string') {
        return item.code.toLowerCase() === inputCode.toLowerCase();
      }
      return false;
    });

    if (selectedDiscount) {
      setVehicleReservationValue('promo_code', selectedDiscount.id);
      setVehicleReservationValue('discount_rate', selectedDiscount.amount);
      setVehicleReservationValue('discount_code', inputCode);
      setVehicleReservationValue('discountApplied', true);
      setValidation(true);
    } else {
      setVehicleReservationValue('promo_code', null);
      setVehicleReservationValue('discount_rate', null);
      setVehicleReservationValue('discountApplied', false);
      setValidation(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputCode(e.target.value);
    setValidation(null);
    setVehicleReservationValue('discountApplied', false); // Reset global applied state when input changes
  };

  return (
    <Box sx={styles.discountContainer}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Enter discount code"
        value={inputCode}
        disabled={isDisableDiscount}
        error={validation === false}
        helperText={validation === false ? 'Invalid discount code' : ''}
        onChange={handleInputChange}
        sx={styles.discountInput}
        InputProps={{
          endAdornment: inputCode.trim() ? (
            <InputAdornment position="end">
              <Button
                onClick={applyDiscount}
                disabled={VehicleReservationMain.discountApplied || isDisableDiscount}
                variant="contained"
                sx={{
                  bgcolor: VehicleReservationMain.discountApplied ? '#9ca3af' : '#ef4444',
                  '&:hover': {
                    bgcolor: VehicleReservationMain.discountApplied ? '#9ca3af' : '#dc2626',
                  },
                  color: '#fff',
                  textTransform: 'none',
                  height: '36px',
                  fontSize: '14px',
                  borderRadius: '6px',
                }}
              >
                {VehicleReservationMain.discountApplied ? 'Applied' : 'Apply'}
              </Button>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};

const styles = {
  discountContainer: {
    width: '100%',
  },
  discountInput: {
    '& .MuiOutlinedInput-root': {
      fontSize: '15px',
      backgroundColor: '#fff',
      borderRadius: '6px',
      '& fieldset': {
        borderColor: '#d1d5db',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#9ca3af',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2563eb',
        borderWidth: '2px',
      },
      '&.Mui-error fieldset': {
        borderColor: '#ef4444',
      },
      '&.Mui-disabled': {
        backgroundColor: '#f9fafb',
        '& fieldset': {
          borderColor: '#e5e7eb',
        },
      },
    },
    '& .MuiInputBase-input': {
      padding: '12px 14px',
      fontSize: '15px',
      color: '#1f2937',
      '&::placeholder': {
        color: '#9ca3af',
        opacity: 1,
      },
      '&.Mui-disabled': {
        color: '#9ca3af',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '13px',
      marginTop: '6px',
      marginLeft: '2px',
      '&.Mui-error': {
        color: '#ef4444',
      },
    },
  },
};

export default DiscountCodes;
